import { TEST_ID_WATCHLIST } from "e2e/testId";
import { SearchIcon, SlidersHorizontalIcon } from "lucide-react-native";
import React, { memo } from "react";
// eslint-disable-next-line no-restricted-imports -- RFC-#6 compound waiver: Button (ButtonIcon/ButtonText children, link variant) AND Input compound with a ref on InputField — non-collapsible…
import { TextInput, TextInputProps } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Badge, BadgeText } from "@/components/atoms/Badge";
// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 compound waiver: Button (ButtonIcon/ButtonText children, link variant) AND Input compound with a ref on InputField — non-collapsible…
import { Button, ButtonIcon, ButtonText } from "@/components/ui/button";
import { Stack } from "@/components/atoms/Stack";
// design-system: compound waiver — non-collapsible input composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 compound waiver: Button (ButtonIcon/ButtonText children, link variant) AND Input compound with a ref on InputField — non-collapsible…
import { Input, InputField, InputIcon, InputSlot } from "@/components/ui/input";

import { TypeFocus } from "@/stores/isFocusSearchInput";

/**
 * Props for SearchbarView component
 */
interface Props {
  inputFieldRef: React.RefObject<TextInput>;
  searchText: string;
  onTextInput: (text: string) => void;
  onFocus?: () => void;
  onOpenFilter: () => void;
  onPressDone?: () => void;
  activeFilterCount: number;
  type: TypeFocus;
  isSmallScreen: boolean;
  isDark: boolean;
}

/**
 * Pure view component for search bar with filter button
 * Displays search input field with filter icon button and active filter count badge
 * @param root0 - Component props
 * @param root0.onTextInput - Handler for text input changes
 * @param root0.searchText - Current search text value
 * @param root0.onFocus - Handler for input focus
 * @param root0.inputFieldRef - Ref for input field element
 * @param root0.activeFilterCount - Number of active filters
 * @param root0.type - Focus type for search bar state
 * @param root0.isSmallScreen - Whether device has small screen
 * @param root0.onPressDone - Handler for done button press
 * @param root0.onOpenFilter - Handler for filter button press
 * @param root0.isDark - Whether dark theme is active
 */
const SearchbarView = ({
  onTextInput,
  searchText,
  onFocus,
  inputFieldRef,
  activeFilterCount,
  type,
  isSmallScreen,
  onPressDone,
  onOpenFilter,
  isDark,
}: Props) => (
  <Stack
    direction="horizontal"
    className={`items-center justify-between gap-2 pb-6 ${!isSmallScreen ? "px-2" : ""}`}
  >
    {type !== "filter" && (
      <Box testID={TEST_ID_WATCHLIST.SEARCH_BAR} className="h-12 flex-1">
        <Input
          size="xl"
          className={`flex-1 ${isDark ? "border-0 border-none bg-surface-action" : "border border-surface-strong bg-surface-subtle"}`}
        >
          <InputSlot className="pl-3">
            <InputIcon
              as={SearchIcon}
              size="xl"
              className={
                isDark ? "text-content-subtle" : "text-content-neutral-strong"
              }
            />
          </InputSlot>
          <InputField
            ref={inputFieldRef as React.Ref<TextInputProps>}
            value={searchText}
            placeholder="Search"
            onChangeText={onTextInput}
            onFocus={onFocus}
            className={`font-body ${isDark ? "" : "bg-transparent text-content-primary placeholder:text-content-muted"}`}
            autoCorrect={false}
          />
        </Input>
      </Box>
    )}
    {/* Filter icon always visible regardless of search focus state (SE-3483) */}
    <Stack>
      {activeFilterCount > 0 && (
        <Stack className="absolute right-0 top-0 z-50 translate-x-1/2 -translate-y-1/3">
          <Badge
            testID={TEST_ID_WATCHLIST.FILTER_BADGE}
            className="size-6 items-center justify-center rounded-full bg-status-info"
            variant="solid"
          >
            <BadgeText className="text-content-on-accent">
              {activeFilterCount}
            </BadgeText>
          </Badge>
        </Stack>
      )}
      <Button
        testID={TEST_ID_WATCHLIST.OPEN_FILTER_OPTION}
        size="xl"
        onPress={onOpenFilter}
        variant="link"
        className="rounded-none px-3 py-2"
        accessible
        accessibilityLabel="Open filter options"
        accessibilityRole="button"
      >
        <ButtonIcon
          as={SlidersHorizontalIcon}
          className="font-body"
          size="lg"
        />
      </Button>
    </Stack>
    {type !== "default" && (
      <Button
        size="xl"
        variant="link"
        onPress={onPressDone}
        className="rounded-none px-3 py-2"
      >
        <ButtonText className="font-body text-title">Done</ButtonText>
      </Button>
    )}
  </Stack>
);

SearchbarView.displayName = "SearchbarView";

export default memo(SearchbarView);
